import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";

const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";
const label = "text-xs font-bold uppercase text-slatey";
const EVENTS = [["application_accepted", "Application accepted"], ["learner_enrolled", "Learner enrolled"], ["learner_started", "Learner started"]];
const COMMISSION_STATUSES = ["potential", "pending", "confirmed", "approved", "payable", "paid"];

export default function BenefitSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/benefit-settings").then((r) => setForm(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { id, version, history, updated_at, ...payload } = form;
      await api.put("/admin/benefit-settings", payload);
      toast.success("Benefit settings saved — a new configuration version was created.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <p className="text-sm text-slatey">Loading…</p>;
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <div data-testid="benefit-settings" className="mx-auto max-w-3xl space-y-6">
      <Link to="/app/admin/entitlements" className="inline-flex items-center gap-2 text-sm font-bold text-slatey hover:text-cobalt"><ArrowLeft className="h-4 w-4" /> Entitlements</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">MyAE benefit settings</h1>
        <p className="mt-1 text-sm text-slatey">Configuration version {form.version} — historical entitlements keep the configuration they were created under.</p>
      </div>

      <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className={label}>Benefit name</p>
            <input value={form.benefit_name || ""} onChange={(e) => update("benefit_name", e.target.value)} className={`${input} mt-1`} data-testid="benefit-name" />
          </div>
          <div>
            <p className={label}>Duration (months)</p>
            <input type="number" value={form.duration_months ?? ""} onChange={(e) => update("duration_months", e.target.value ? Number(e.target.value) : null)} className={`${input} mt-1`} data-testid="benefit-duration" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={Boolean(form.active)} onChange={(e) => update("active", e.target.checked)} className="h-4 w-4 accent-cobalt" data-testid="benefit-active" /> Benefit active
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className={label}>Qualifying outcome event</p>
            <select value={form.qualifying_outcome_event} onChange={(e) => update("qualifying_outcome_event", e.target.value)} className={`${input} mt-1`} data-testid="benefit-outcome-event">
              {EVENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <p className={label}>Required commission status</p>
            <select value={form.required_commission_status} onChange={(e) => update("required_commission_status", e.target.value)} className={`${input} mt-1`} data-testid="benefit-commission-status">
              {COMMISSION_STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
            </select>
          </div>
          <div>
            <p className={label}>Provisioning behaviour</p>
            <select value={form.provisioning_behaviour} onChange={(e) => update("provisioning_behaviour", e.target.value)} className={`${input} mt-1`} data-testid="benefit-provisioning">
              <option value="automatic_simulated">Automatic (simulated — no external call)</option>
              <option value="manual">Manual (admin provisions)</option>
            </select>
          </div>
        </div>
        <div>
          <p className={label}>Eligible provider IDs (comma-separated, blank = all)</p>
          <input value={(form.eligible_provider_ids || []).join(", ")} onChange={(e) => update("eligible_provider_ids", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={`${input} mt-1`} data-testid="benefit-providers" />
        </div>
        <div>
          <p className={label}>Exclusions</p>
          <textarea value={form.exclusions || ""} onChange={(e) => update("exclusions", e.target.value)} rows={2} className={`${input} mt-1`} data-testid="benefit-exclusions" />
        </div>
      </section>

      <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-xl font-bold text-ink">Learner-facing copy</h2>
        <div>
          <p className={label}>Headline</p>
          <input value={form.learner_headline || ""} onChange={(e) => update("learner_headline", e.target.value)} className={`${input} mt-1`} data-testid="benefit-headline" />
        </div>
        <div>
          <p className={label}>Description</p>
          <textarea value={form.learner_description || ""} onChange={(e) => update("learner_description", e.target.value)} rows={2} className={`${input} mt-1`} data-testid="benefit-description" />
        </div>
        <div>
          <p className={label}>"What is MyAE?" popup text</p>
          <textarea value={form.popup_text || ""} onChange={(e) => update("popup_text", e.target.value)} rows={3} className={`${input} mt-1`} data-testid="benefit-popup" />
        </div>
      </section>

      <button onClick={save} disabled={busy} data-testid="benefit-save"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3.5 text-sm font-bold text-white disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save settings (new version)
      </button>

      <section className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="benefit-history">
        <h2 className="font-display text-xl font-bold text-ink">Configuration history</h2>
        <ul className="mt-3 space-y-2">
          {(form.history || []).slice().reverse().map((entry, index) => (
            <li key={index} className="rounded-2xl bg-surface px-4 py-3 text-xs text-slatey">
              <span className="font-bold text-ink">v{entry.version}</span> · {entry.note} · {entry.by} · {entry.at?.slice(0, 16).replace("T", " ")}
            </li>
          ))}
          {!(form.history || []).length && <li className="text-sm text-slatey">No changes yet — version {form.version} is the initial configuration.</li>}
        </ul>
      </section>
    </div>
  );
}
